import { createTheme, type Shadows, type Theme } from "@mui/material";

import { getIsWorkerAppRebrandingEnabled } from "../internal/workerAppRebranding";
import { muiThemePalette } from "./colors";
import { muiThemeComponents } from "./components";
import { muiRebrandThemePalette } from "./rebrandColors";
import { muiRebrandThemeTypography } from "./rebrandTypography";
import { muiThemeTypography } from "./typography";

declare module "@mui/material/styles" {
  interface CustomBorderRadius {
    borderRadius?: {
      xSmall: string;
      small: string;
      medium: string;
      large: string;
      xLarge: string;
      leftChatMessage: string;
      rightChatMessage: string;
    };
  }

  interface CustomSize {
    size?: {
      avatar: {
        small: string;
        medium: string;
        large: string;
      };
      button: {
        small: {
          height: string;
          iconSize: string;
        };
        medium: {
          height: string;
          iconSize: string;
        };
        large: {
          height: string;
          iconSize: string;
        };
      };
      checkbox: {
        width: string;
        height: string;
      };
      dateCell: {
        small: {
          width: string;
          height: string;
        };
        medium: {
          width: string;
          height: string;
        };
      };
      calendarCell: {
        width: string;
        height: string;
      };
      icon: {
        xSmall: string;
        small: string;
        medium: string;
        large: string;
        xLarge: string;
      };
      iconButton: {
        xSmall: {
          size: string;
          iconSize: string;
        };
        small: {
          size: string;
          iconSize: string;
        };
        medium: {
          size: string;
          iconSize: string;
        };
        large: {
          size: string;
          iconSize: string;
        };
      };
      loadingSpinner: {
        small: string;
        medium: string;
        large: string;
      };
      pill: {
        small: string;
        medium: string;
      };
      input: {
        small: string;
        medium: string;
        large: string;
      };
      tabs: {
        height: string;
        lineHeight: string;
        indicatorHeight: string;
      };
      shiftCard: {
        minHeight: string;
      };
      documents: {
        card: {
          minHeight: string;
        };
        bannerWithAction: {
          minHeight: string;
        };
      };
      bottomSheet: {
        loading: {
          minHeight: string;
        };
        review: {
          minHeight: string;
        };
      };
      navbar: {
        itemSize: string;
        iconSize: string;
        labelSize: string;
      };
    };
  }

  interface CustomBorderWidth {
    borderWidth: {
      thin: string;
      regular: string;
      thick: string;
      xThick: string;
    };
  }

  interface CustomProps extends CustomBorderRadius, CustomSize, CustomBorderWidth {}

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Theme extends CustomProps {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ThemeOptions extends CustomProps {}
}

export function getTheme(): Theme {
  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    transitions: {
      duration: {
        leavingScreen: 225,
        enteringScreen: 225,
      },
    },
    borderRadius: {
      xSmall: "12px",
      small: "16px",
      medium: "24px",
      large: "32px",
      xLarge: "100px",
      leftChatMessage: "24px 24px 24px 4px",
      rightChatMessage: "24px 24px 4px 24px",
    },
    size: {
      avatar: {
        small: "2rem", // 32px
        medium: "4rem", // 64px
        large: "6.5rem", // 104px
      },
      button: {
        small: {
          height: "2.5rem", // 40px
          iconSize: "1rem", // 16px
        },
        medium: {
          height: "3rem", // 48px
          iconSize: "1.25rem", // 20px
        },
        large: {
          height: "4rem", // 64px
          iconSize: "1.25rem", // 20px
        },
      },
      calendarCell: {
        width: "3rem", // 48px
        height: "4rem", // 64px
      },
      checkbox: {
        width: "1.5rem", // 24px
        height: "1.5rem", // 24px
      },
      dateCell: {
        small: {
          width: "2.875rem", // 46px
          height: "4rem", // 64px
        },
        medium: {
          width: "4rem", // 64px
          height: "4.875rem", // 78px
        },
      },
      iconButton: {
        xSmall: {
          size: "1.25rem", // 20px
          iconSize: "0.875rem", // 14px
        },
        small: {
          size: "2.5rem", // 40px
          iconSize: "1.25rem", // 20px
        },
        medium: {
          size: "3rem", // 48px
          iconSize: "1.5rem", // 24px
        },
        large: {
          size: "4rem", // 64px
          iconSize: "1.5rem", // 24px
        },
      },
      icon: {
        xSmall: "0.75rem", // 12px
        small: "1rem", // 16px
        medium: "1.5rem", // 24px
        large: "2rem", // 32px
        xLarge: "3.5rem", // 56px
      },
      loadingSpinner: {
        small: "1.5rem", // 24px
        medium: "2.5rem", // 40px
        large: "4rem", // 64px
      },
      pill: {
        small: "1.75rem", // 28px
        medium: "2rem", // 32px
      },
      input: {
        small: "2.5rem", // 40px
        medium: "3rem", // 48px
        large: "4rem", // 64px
      },
      tabs: {
        height: "2.5rem", // 40px
        lineHeight: "1rem", // 16px
        indicatorHeight: "3px",
      },
      shiftCard: {
        minHeight: "10.5rem", // 168px
      },
      documents: {
        card: {
          minHeight: "5.75rem", // 92px
        },
        bannerWithAction: {
          minHeight: "12.5rem", // 200px
        },
      },
      bottomSheet: {
        loading: {
          minHeight: "24.1875rem", // 387px
        },
        review: {
          minHeight: "35.625rem", // 570px
        },
      },
      navbar: {
        // using px intentionally as we don't want to scale navbar on device zoom
        itemSize: "48px",
        iconSize: "24px",
        labelSize: "10px",
      },
    },
    borderWidth: {
      thin: "0.5px",
      regular: "1px",
      thick: "2px",
      xThick: "4px",
    },
    zIndex: {
      // (TODO): Check if this is still relevant
      appBar: 2000,
    },
    spacing: [0, 4, 6, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64],
    shadows: [
      "none",
      "0px 0px 1px 0px rgba(0, 0, 0, 0.2)",
      "0px 4px 24px 0px rgba(0, 0, 0, 0.04)",
      "0px 4px 40px 0px rgba(0, 0, 0, 0.15)",
      "0px -10px 20px 0px rgba(0, 0, 0, 0.05)",
      // We don't support higher elevations than 4
      ...Array.from<string>({ length: 20 }).fill("none"),
    ] as Shadows,
    typography: getIsWorkerAppRebrandingEnabled() ? muiRebrandThemeTypography : muiThemeTypography,
    palette: getIsWorkerAppRebrandingEnabled() ? muiRebrandThemePalette : muiThemePalette,
    components: muiThemeComponents,
  });
}
